// Olympe
import { useProperty } from '@olympeio/core';

// Rxjs
import { tap, combineLatest, map, distinctUntilChanged } from 'rxjs';

// Internal
import { generateCell, getCellStyle } from './cells';
import { trimPath } from './helpers';
import { getColumns } from './columns';
import { getFlattenWrappers } from './wrappers';

/**
 * Init the rows
 * @param {!BrickContext} $ 
 * @param {!Observable} _wrappers 
 * @param {!Observable} _filteredColumns 
 */
export function initRows($, _wrappers, _filteredColumns) {
    // Listen to the _data and the _columns
    combineLatest([
        _wrappers,
        _filteredColumns
            .pipe(distinctUntilChanged(undefined, filteredColumns => {
                // Avoid updating the rows for some changes on the column config: width, resizable, etc.
                return Array.from(filteredColumns.values())
                    .flat()
                    .map(c => `${c.Header}${c['Property Path']}${c.Type}${c.Options}${c.Rank}${c.Editable}${c.Level}`)
                    .join('');
            })),
        $.observe('Row Height', false),
        $.observe('Cell Edition', false),
        $.observe('Disable Header Row', false),
        $.observe('Default Color', false),
        $.observe('Border Color', false),
        $.observe('Border Width', false)
    ])

        // Use the reactgrid format for rows
        .pipe(map(([_wrappers, _filteredColumns, rowHeight, cellEdition, removeHeaderRow]) => {
            // Optional header row
            const headerRow = removeHeaderRow ? [] : [{
                rowId: '_header',
                height: rowHeight,
                cells: getColumns($, 0).map(column => {
                    const text = column.Header ?? trimPath(column['Property Path'] ?? '');
                    const finalText = column.Level === 0 ? text : '';
                    return { type: 'header', text: finalText, style: getCellStyle($) };
                })
            }];

            // Generate all the rows
            const rows = getFlattenWrappers($).map(({ wrapper, level }, index) => {
                const rowColumns = getColumns($, level);
                return generateRow(wrapper, rowColumns, index, $, rowHeight, cellEdition);
            });

            // Final rows
            return [headerRow, rows].flat();
        }))

        // Set the final rows
        .pipe(tap(rows => $.set('_rgRows', rows)))

        // Run 
        .subscribe();
}

/**
 * @param {!BrickContext}
 * @return {!*}
 */
export function useRows($) {
    return useProperty($, '_rgRows') ?? [];
}

/**
 * @param {!RGWrapper} wrapper 
 * @param {!Array} columns 
 * @param {number} index 
 * @param {!BrickContext} $ 
 * @param {number} rowHeight 
 * @param {boolean} cellEdition 
 * @return {!Object}
 */
function generateRow(wrapper, columns, index, $, rowHeight, cellEdition) {
    return { 
        rowId: index,
        height: rowHeight,
        cells: columns.map(column => generateCell($, wrapper, index, column, cellEdition))
    };
}
